import { Link } from "react-router-dom";
import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import BookCTA from "@/components/BookCTA";
import { usePageMeta } from "@/lib/seo";
import { Star, Quote, ArrowUpRight, ArrowRight, MessageCircle } from "lucide-react";

const REVIEW_URL = "https://www.google.com/search?q=waites+clearances+peterborough";

// Written testimonials — we'll add real ones as they come in.
// Set to [] to show the empty state; add objects to display cards.
const TESTIMONIALS = [
  {
    name: "Tiff May",
    location: "Peterborough",
    body: "Great service from start to finish! Very friendly and professional. Would definitely recommend!",
  },
  {
    name: "Ellie Mayo",
    location: "",
    body: "Great price. Very good, recommend fast and friendly. Thanks guys.",
  },
  {
    name: "Liberty Bothamley",
    location: "",
    body: "Really lovely and friendly group of people. Helped me with removing unwanted garden furniture.",
  },
  {
    name: "Angela Elleston",
    location: "",
    body: "Very friendly reliable and professional company would highly recommend",
  },
  {
    name: "Justyna Kowalska",
    location: "",
    body: "Super reliable, friendly and did excellent job. Thank you",
  },
  {
    name: "Fran Alana",
    location: "",
    platform: "facebook",
    body: "Anyone needing any sort of clearance - these guys are amazing. When we recently lost my mum I had the hard task of clearing out her home. Trying to do my research - I can confirm there are a lot of cowboys ready to fleece you when you need clearance... These are the exact opposite - wonderful people - Kind, Compassionate. Professional and completely trustworthy. I can't thank them enough for the pressure they removed from Drapes and I.... And the amazing job they did.",
  },
];

function FacebookF({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-label="Facebook review" role="img">
      <circle cx="12" cy="12" r="12" fill="#1877F2" />
      <path fill="#fff" d="M13.4 19.5v-6.1h2.05l.3-2.4H13.4V9.5c0-.7.2-1.17 1.2-1.17h1.27V6.2a17 17 0 0 0-1.85-.1c-1.83 0-3.08 1.12-3.08 3.17V11H8.88v2.4h2.06v6.1z" />
    </svg>
  );
}

function Stars({ size = 18 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="text-[#F5B400]"
          fill="#F5B400"
          strokeWidth={0}
          style={{ width: size, height: size }}
        />
      ))}
    </span>
  );
}

function GoogleG({ className = "" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function Reviews() {
  usePageMeta(
    "Reviews | House Clearance Peterborough | Waites Clearances",
    "Read reviews from local families across Peterborough who trusted Waites Clearances with their house clearance. Family run, based in Peterborough."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-reviews">
      <Header />

      <PageHero
        eyebrow="Reviews"
        title="Loved by local families across Peterborough."
        description="We're a small, family run team, and word of mouth means everything to us. If we've done a good job for you, we'd love it if you left a note on Google — and if we haven't, we want to know so we can put it right."
      />

      {/* Rating summary card */}
      <section className="py-14 sm:py-16" data-testid="reviews-summary">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] via-white to-white p-7 sm:p-10">
            <div className="absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#FF7F00]/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0">
                  <GoogleG className="h-9 w-9" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B85800]">
                    Rated on Google
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-display text-4xl font-extrabold text-slate-900 leading-none">
                      5.0
                    </span>
                    <Stars size={22} />
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600">
                    10 reviews from local customers
                  </p>
                </div>
              </div>

              <div className="hidden md:block h-16 w-px bg-slate-200" />

              <div className="flex-1">
                <p className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug">
                  Every review helps a family like yours choose with confidence.
                </p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  It only takes a minute and it means the world to a small team like ours.
                </p>
              </div>

              <a
                href={REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-cta-leave"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors shrink-0"
              >
                Leave us a review on Google
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="pb-16" data-testid="reviews-testimonials">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
              In their words
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Kind words from our customers.
            </h2>
          </div>

          {TESTIMONIALS.length === 0 ? (
            <div
              data-testid="reviews-empty"
              className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-white p-10 sm:p-14 text-center"
            >
              <div className="mx-auto h-14 w-14 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-[#FF7F00]" />
              </div>
              <h3 className="mt-5 font-display text-xl sm:text-2xl font-bold text-slate-900">
                Written testimonials coming soon.
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed max-w-lg mx-auto">
                We're gathering written notes from recent customers and will publish them here as
                soon as they arrive. In the meantime you can read (and leave) reviews directly on
                Google.
              </p>
              <a
                href={REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-empty-cta"
                className="mt-7 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0F172A] hover:bg-[#1F2937] text-white font-bold transition-colors"
              >
                See Google reviews
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={i}
                  data-testid={`review-card-${i}`}
                  className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 hover:border-[#FFB870] hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between">
                    <Stars size={16} />
                    {t.platform === "facebook" ? (
                      <FacebookF className="h-5 w-5" />
                    ) : (
                      <GoogleG className="h-5 w-5 opacity-80" />
                    )}
                  </div>
                  <Quote className="mt-4 h-6 w-6 text-[#FFB870]" />
                  <p className="mt-2 text-slate-700 leading-relaxed">{t.body}</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center font-display font-bold text-[#B85800]">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-display font-semibold text-slate-900 leading-tight">
                        {t.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t.platform === "facebook" ? "Facebook review" : t.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ask-for-a-review card */}
      <section className="pb-20" data-testid="reviews-ask">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-12">
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
              <div className="lg:col-span-8">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
                  Recently used us?
                </p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Please leave us a review.
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-xl">
                  Every review helps another family in Peterborough choose with confidence — and it
                  keeps us on our toes. A minute of your time makes a huge difference.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
                <a
                  href={REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reviews-ask-google"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors"
                >
                  Leave a Google review
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/contact"
                  data-testid="reviews-ask-feedback"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-slate-200 bg-white text-slate-800 hover:border-[#FF7F00] hover:text-[#FF7F00] font-semibold transition-colors"
                >
                  Send private feedback
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookCTA
        title="Not booked with us yet?"
        body="Try Peterborough's family run clearance team for yourself — most of our new customers arrive from a neighbour's recommendation."
      />

      <SiteFooter />
    </div>
  );
}
